// ============================================================================
// IngestionPanel.jsx
// Upload for both Time and Wage Audit feeds, lifted out of the dashboard onto
// its own page under Data Uploads. The two are one panel because they are one
// pipeline: both write to time_wage_audit_batches and share the
// preview -> confirm flow, so a single batch history is the honest view of what
// has been loaded. Two panels reading the same table would each show a part.
// ============================================================================

import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle, XCircle, Clock, Upload } from 'lucide-react';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtNumber, fmtMiles } from './format';
import { card, btn, tbl } from '../../theme';

const BATCH_STATUS_STYLE = {
  committed: { color: '#16A34A', bg: '#DCFCE7', icon: <CheckCircle size={12} /> },
  preview:   { color: '#1D4ED8', bg: '#EFF6FF', icon: <Clock size={12} /> },
  cancelled: { color: '#9CA3AF', bg: '#F3F4F6', icon: <XCircle size={12} /> },
  failed:    { color: '#DC2626', bg: '#FEE2E2', icon: <XCircle size={12} /> },
};

// Both feeds land in time_wage_audit_batches and share the preview -> confirm
// flow, so they share this panel. Only the endpoints and what the preview is
// worth saying about differ, and that lives here rather than in two branches
// scattered through the JSX.
const FEEDS = {
  meal: {
    label: 'Meal break discrepancies',
    source: 'Samsara Studio export',
    base: '/time-wage-audit',
    blurb: 'Records are matched by their stable key and upserted — new discrepancies are added, '
         + 'existing ones refreshed, and any marked retracted upstream (e.g. a corrected punch) drop '
         + 'out of the active view. Prior confirm/dismiss decisions are never overwritten by an import.',
  },
  post_trip: {
    label: 'Post-trip lapses',
    source: 'post-trip export',
    base: '/time-wage-audit/post-trip',
    blurb: 'One row per driver per shift, whether or not anything happened — the rows with no '
         + 'measurable close-out are kept deliberately, because they are the denominator for the '
         + 'coverage figure the page reports. Matched and upserted on the export\'s own key.',
  },
};

export default function IngestionPanel({ onCommitted }) {
  const qc = useQueryClient();
  const [feed, setFeed]         = useState('meal');
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const cfg = FEEDS[feed];

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['twa-batches'],
    queryFn:  () => api.get('/time-wage-audit/batches'),
    refetchInterval: 15000,
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload(`${cfg.base}/upload`, fd, setProgress);
    },
    onSuccess: (data) => {
      setPreview(data);
      toast.success(`Parsed ${fmtNumber(data.preview.total_rows)} rows — review before syncing.`);
      setProgress(0);
      qc.invalidateQueries({ queryKey: ['twa-batches'] });
    },
    onError: (err) => { toast.error(err.message || 'Upload failed.'); setProgress(0); },
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.post(`${cfg.base}/upload/confirm`, { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(feed === 'meal'
        ? `Synced ${fmtNumber(data.newCount)} new, ${fmtNumber(data.updatedCount)} updated, ${fmtNumber(data.retractedCount)} retracted.`
        : `Synced ${fmtNumber(data.newCount)} new, ${fmtNumber(data.updatedCount)} updated.`);
      resetUpload();
      qc.invalidateQueries({ queryKey: ['twa-batches'] });
      onCommitted?.();
    },
    onError: (err) => toast.error(err.message || 'Failed to sync batch.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`${cfg.base}/upload/cancel`, { batch_id: preview.batch_id }),
    onSuccess: () => { toast.success('Batch discarded.'); resetUpload(); qc.invalidateQueries({ queryKey: ['twa-batches'] }); },
    onError:   (err) => toast.error(err.message || 'Failed to discard batch.'),
  });

  function resetUpload() {
    setFile(null); setPreview(null); setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview;
  const busy = uploadMutation.isPending || confirmMutation.isPending || cancelMutation.isPending;

  return (
    <div style={s.ingestSection}>
      <div style={s.hd2}>
        <h2 style={s.title2}>Sync data</h2>
        <p style={s.sub}>Upload the latest {cfg.source}. {cfg.blurb}</p>
      </div>

      {/* Switching feeds discards any in-progress preview: a staged batch
          belongs to the feed that parsed it, and confirming it against the
          other one is refused server-side anyway. */}
      <div style={s.feedTabs}>
        {Object.entries(FEEDS).map(([k, f]) => (
          <button
            key={k}
            style={{ ...s.feedTab, ...(feed === k ? s.feedTabOn : null) }}
            onClick={() => { if (feed !== k) { setFeed(k); resetUpload(); } }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={s.card}>
        {!preview ? (
          <>
            <div
              style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : '#D1D5DB' }}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
            >
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                     onChange={(e) => setFile(e.target.files[0] || null)} />
              <Upload size={24} color={file ? '#16A34A' : '#9CA3AF'} />
              <div style={s.dropText}>
                {file ? (
                  <><strong style={{ color: '#16A34A' }}>{file.name}</strong><br /><span style={{ color: '#9CA3AF' }}>{(file.size / 1024).toFixed(0)} KB</span></>
                ) : (
                  <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: '#9CA3AF' }}>{cfg.source} — .csv, .xlsx, or .xls</span></>
                )}
              </div>
            </div>

            {uploadMutation.isPending && (
              <div style={s.progressWrap}>
                <div style={s.progressTrack}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
                <span style={s.progressLabel}>{progress}%</span>
              </div>
            )}

            <button
              style={{ ...s.uploadBtn, opacity: file && !uploadMutation.isPending ? 1 : 0.5 }}
              disabled={!file || uploadMutation.isPending}
              onClick={() => uploadMutation.mutate()}
            >
              {uploadMutation.isPending ? 'Parsing…' : '↑ Upload and Preview'}
            </button>
          </>
        ) : (
          <div>
            {feed === 'meal' ? (
              <div style={s.previewGrid}>
                <PreviewStat label="Total Rows" value={fmtNumber(p.total_rows)} />
                <PreviewStat label="New" value={fmtNumber(p.new_count)} />
                <PreviewStat label="Updated" value={fmtNumber(p.updated_count)} />
                <PreviewStat label="Retracted" value={fmtNumber(p.retracted_count)} warn={p.retracted_count > 0} />
                <PreviewStat label="Unmapped Cost Center" value={fmtNumber(p.unmapped_cost_center_count)} warn={p.unmapped_cost_center_count > 0} />
                <PreviewStat label="Total Miles" value={fmtMiles(p.total_miles)} />
              </div>
            ) : (
              <>
                {/* Coverage first. A post-trip file is mostly rows with no
                    measurable close-out by design, so "13,316 rows" on its own
                    would read as far more signal than it is. */}
                <div style={s.previewGrid}>
                  <PreviewStat label="Total Shifts" value={fmtNumber(p.total_rows)} />
                  <PreviewStat label="Measurable" value={`${p.measurable_pct}%`}
                               warn={Number(p.measurable_pct) < 5} />
                  <PreviewStat label="With a close-out" value={fmtNumber(p.measured_count)} />
                  <PreviewStat label="Drivers" value={fmtNumber(p.driver_count)} />
                  <PreviewStat label="Unmapped Cost Center" value={fmtNumber(p.unmapped_cost_center_count)}
                               warn={p.unmapped_cost_center_count > 0} />
                  <PreviewStat label="Dates" value={`${p.date_from || '—'} → ${p.date_to || '—'}`} />
                </div>
                <div style={s.breakdown}>
                  <span style={s.breakdownLabel}>Categories</span>
                  {Object.entries(p.by_category || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <span key={k} style={s.breakdownChip}>{k.replace(/_/g, ' ')} {fmtNumber(n)}</span>
                  ))}
                </div>
                <div style={s.breakdown}>
                  <span style={s.breakdownLabel}>Completeness</span>
                  {Object.entries(p.by_completeness || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <span key={k} style={s.breakdownChip}>{k.replace(/_/g, ' ')} {fmtNumber(n)}</span>
                  ))}
                </div>
              </>
            )}

            {p.unmapped_cost_center_count > 0 && (
              <div style={s.warningBox}>
                <AlertTriangle size={14} color="#92400E" />
                <span>{fmtNumber(p.unmapped_cost_center_count)} row(s) had a cost center code we couldn't resolve. They'll still sync, just without a cost center.</span>
              </div>
            )}
            {p.warning_count > 0 && (
              <div style={s.warningBox}>
                <AlertTriangle size={14} color="#92400E" />
                {/* Post-trip warnings name the offending value (an unknown
                    category, an unmapped column) and the row still imports, so
                    showing them beats sending someone to a server log. */}
                {feed === 'post_trip' && Array.isArray(p.warnings) && p.warnings.length
                  ? <span>{p.warnings.slice(0, 4).join(' ')}{p.warnings.length > 4
                      ? ` (+${p.warnings.length - 4} more)` : ''}</span>
                  : <span>{fmtNumber(p.warning_count)} row(s) skipped — see server log for details.</span>}
              </div>
            )}

            <div style={s.previewActions}>
              <button style={s.cancelBtn} onClick={() => cancelMutation.mutate()} disabled={busy}>Discard</button>
              <button style={s.syncBtn} onClick={() => confirmMutation.mutate()} disabled={busy}>
                {confirmMutation.isPending ? 'Syncing…' : 'Confirm & Sync'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <table style={s.logTable}>
            <thead>
              <tr>{['File', 'Feed', 'Status', 'Rows', 'New', 'Updated', 'Retracted', 'By', 'When'].map((h) => <th key={h} style={s.logTh}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const st = BATCH_STATUS_STYLE[b.status] || BATCH_STATUS_STYLE.preview;
                return (
                  <tr key={b.id} style={s.logTr}>
                    <td style={s.logTd} title={b.file_name}>{b.file_name?.slice(-40)}</td>
                    <td style={s.logTd}>{FEEDS[b.dataset]?.label || 'Meal break discrepancies'}</td>
                    <td style={s.logTd}><span style={{ ...s.statusPill, color: st.color, background: st.bg }}>{st.icon} {b.status}</span></td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{fmtNumber(b.row_count)}</td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{fmtNumber(b.new_count)}</td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{fmtNumber(b.updated_count)}</td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{fmtNumber(b.retracted_count)}</td>
                    <td style={s.logTd}>{b.uploaded_by_name || '—'}</td>
                    <td style={s.logTd}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
              {!batches.length && <tr><td colSpan={9} style={s.emptyLog}>No imports yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Left behind when this panel was extracted onto its own page: it lived below
// KPICard in the old file, outside the block that moved. Every preview render
// then hit an undefined component and threw, which is why a successful upload
// turned the page white. Lint stayed green because react/jsx-no-undef is off,
// so an undefined COMPONENT is not reported the way an undefined variable is.
function PreviewStat({ label, value, warn }) {
  return (
    <div style={s.previewStat}>
      <div style={{ ...s.previewStatValue, color: warn ? '#92400E' : '#111827' }}>{value}</div>
      <div style={s.previewStatLabel}>{label}</div>
    </div>
  );
}

const s = {
  previewStat: { textAlign: 'center' },
  previewStatValue: { fontSize: '20px', fontWeight: 700 },
  previewStatLabel: { fontSize: '11px', color: '#6B7280', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  ingestSection: { marginTop: '8px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' },
  hd2:         { marginBottom: '16px' },
  title2:      { fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '4px' },
  sub:         { fontSize: '13px', color: '#6B7280', lineHeight: 1.6 },
  feedTabs:    { display: 'flex', gap: '6px', marginBottom: '12px' },
  feedTab:     { ...btn.secondarySm },
  feedTabOn:   { background: '#1B2A4A', color: '#E8EEF7', borderColor: '#1B2A4A' },
  card:        { ...card.base, overflow: 'hidden', marginBottom: '24px' },
  dropZone: {
    border: '2px dashed', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer',
    marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', transition: 'border-color 0.2s',
  },
  dropText:     { fontSize: '13px', color: '#374151', lineHeight: 1.6 },
  progressWrap: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', background: '#1B2A4A', borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '11px', fontWeight: 600, color: '#374151', minWidth: '30px' },
  uploadBtn: { ...btn.primary, transition: 'opacity 0.15s' },
  previewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', padding: '20px', paddingBottom: '0' },
  breakdown:   { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '10px' },
  breakdownLabel: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.12em', color: '#9CA3AF', marginRight: '2px' },
  breakdownChip:  { fontSize: '11px', color: '#374151', background: '#F3F4F6',
                    border: '1px solid #E5E7EB', borderRadius: '999px', padding: '2px 8px',
                    fontVariantNumeric: 'tabular-nums' },
  warningBox: {
    display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '16px 20px 0',
    padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', fontSize: '12px', color: '#92400E', lineHeight: 1.5,
  },
  previewActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '20px' },
  cancelBtn: { ...btn.secondarySm },
  syncBtn:   { ...btn.primarySm },
  logSection:  { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' },
  logTitle:    { fontSize: '13px', fontWeight: 600, color: '#374151', padding: '14px 18px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  logTable:    { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  logTh:       { ...tbl.th, ...tbl.th },
  logTr:       { borderBottom: '1px solid #F3F4F6' },
  logTd:       { ...tbl.td, ...tbl.td },
  statusPill:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
};
